import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { ApiError } from "../../api/client";
import { fetchContent } from "../../api/content";
import {
  GIG_PAGE_SIZE,
  fetchGigBoard,
  fetchGigBoardHome,
  logGigApply,
  reportWin,
  setApplicationStatus,
  submitCheckin,
  type GigApplication,
  type GigBoardCounts,
  type GigBoardFilter,
  type GigBoardHome,
  type GigListing,
  type WinPaid,
} from "../../api/gigboard";
import type { Track } from "../../api/types";
import { BackButton } from "../../components/BackButton";
import { Banner } from "../../components/Banner";
import { ScreenHeader } from "../../components/ScreenHeader";
import { ScreenState } from "../../components/ScreenState";
import { attempt, messageOf } from "../../lib/errors";
import { useSession } from "../../lib/session";
import { useAsync } from "../../lib/useAsync";
import { useDebouncedCallback } from "../../lib/useDebounced";
import { CheckinModal, type CheckinResult } from "../../screens/gigboard/CheckinModal";
import {
  GigBoardEmpty,
  GigBoardScreen,
  type GigTab,
  type GigTile,
} from "../../screens/gigboard/GigBoardScreen";
import { ReportWinScreen } from "../../screens/gigboard/ReportWinScreen";
import { toWinInput, type WinDraft } from "../../screens/gigboard/WinFields";
import { DoneScreen } from "./DoneScreen";

const DEFAULT_FILTER: GigBoardFilter = {
  view: "rec",
  kind: "any",
  source: null,
  sort: "mixed",
  englishOnly: true,
  search: "",
  page: 1,
  size: GIG_PAGE_SIZE,
};

const trackLabelsOf = (tracks: Record<Track, { label: string }>) =>
  Object.fromEntries(Object.entries(tracks).map(([key, t]) => [key, t.label])) as Record<
    Track,
    string
  >;

/** The Gig Board is closed until Step 5 is done: the server says why. */
function LockedBoard({ error }: { error: Error }) {
  return (
    <div>
      <ScreenHeader kicker="Step 6 · Gig Board" title="Not open yet." sub={error.message} />
      <BackButton to="/gig/5" label="Go to Step 5" />
    </div>
  );
}

export function GigBoardRoute() {
  const { user } = useSession();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // The first page loads with the home: getGigBoard refuses with the reason when the board isn't open yet.
  const loaded = useAsync(
    () => Promise.all([fetchGigBoardHome(), fetchContent(), fetchGigBoard(DEFAULT_FILTER)]),
    [],
  );

  const [home, setHome] = useState<GigBoardHome | null>(null);
  const [tab, setTab] = useState<GigTab>("all");
  const [filter, setFilter] = useState<GigBoardFilter>(DEFAULT_FILTER);
  const [tiles, setTiles] = useState<GigTile[]>([]);
  const [total, setTotal] = useState(0);
  const [sources, setSources] = useState<string[]>([]);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [counts, setCounts] = useState<GigBoardCounts | null>(null);
  const [loadingGigs, setLoadingGigs] = useState(false);
  const [gigsError, setGigsError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [checkinOpen, setCheckinOpen] = useState(false);
  const [checkinBusy, setCheckinBusy] = useState(false);
  const [checkinError, setCheckinError] = useState<string | null>(null);
  /* Every request gets a number; only the newest one's answer is shown, so a slow page can't overwrite new filters. */
  const seq = useRef(0);

  useEffect(() => {
    if (!loaded.data) return;
    const [nextHome, , page] = loaded.data;
    setHome(nextHome);
    setTiles(page.tiles);
    setTotal(page.total);
    setSources(page.sources);
    setUpdatedAt(page.updatedAt ?? null);
    setCounts(page.counts);
    // v2: the check-in pops up at the first visit on or after Friday (and from Friday's reminder).
    if (nextHome.checkinDue) setCheckinOpen(true);
  }, [loaded.data]);

  const load = useCallback(async (next: GigBoardFilter, append: boolean) => {
    const mine = ++seq.current;
    setLoadingGigs(true);
    setGigsError(null);
    try {
      const page = await fetchGigBoard(next);
      if (mine !== seq.current) return;
      setTiles((current) => (append ? [...current, ...page.tiles] : page.tiles));
      setTotal(page.total);
      setSources(page.sources);
      setUpdatedAt(page.updatedAt ?? null);
      setCounts(page.counts);
    } catch (e) {
      if (mine === seq.current) setGigsError(messageOf(e));
    } finally {
      if (mine === seq.current) setLoadingGigs(false);
    }
  }, []);

  const loadSoon = useDebouncedCallback((next: GigBoardFilter) => void load(next, false), 250);

  const recordApply = (listing: GigListing) => {
    // Fire and forget: the listing has already opened in a new tab.
    void logGigApply(listing.listingKey)
      .then((application) => {
        setTiles((current) =>
          current.map((tile) =>
            tile.listing.listingKey === listing.listingKey ? { ...tile, application } : tile,
          ),
        );
        setHome((current) => {
          if (!current) return current;
          const known = current.applications.some((a) => a.id === application.id);
          return {
            ...current,
            applications: known
              ? current.applications.map((a) => (a.id === application.id ? application : a))
              : [application, ...current.applications],
            appliedThisWeek: current.appliedThisWeek + (known ? 0 : 1),
          };
        });
      })
      .catch((e: unknown) => setError(messageOf(e)));
  };

  const replaceApplication = (application: GigApplication) => {
    setHome((current) =>
      current
        ? {
            ...current,
            applications: current.applications.map((a) =>
              a.id === application.id ? application : a,
            ),
          }
        : current,
    );
    setTiles((current) =>
      current.map((tile) =>
        tile.application?.id === application.id ? { ...tile, application } : tile,
      ),
    );
  };

  const finishCheckin = async (result: CheckinResult): Promise<boolean> => {
    const saved = await attempt(setCheckinBusy, setCheckinError, () =>
      submitCheckin({
        answers: result.answers,
        otherWork: result.otherWork,
        win: result.win ? toWinInput(result.win) : null,
      }),
    );
    if (!saved) return false;
    setHome(saved);
    return true;
  };

  if (!loaded.data || !home || !counts || !user?.track) {
    const forbidden = loaded.error instanceof ApiError && loaded.error.code === "FORBIDDEN";
    return (
      <ScreenState
        loading={loaded.loading || (!loaded.error && !home)}
        error={loaded.error}
        onRetry={loaded.reload}
        renderError={forbidden ? (e) => <LockedBoard error={e} /> : undefined}
      >
        {null}
      </ScreenState>
    );
  }

  const trackLabels = trackLabelsOf(loaded.data[1].tracks);
  const myTrack = user.track;

  if (!home.open) {
    return (
      <LockedBoard
        error={new Error("The Gig Board opens once you upload your certificate in Step 5.")}
      />
    );
  }

  return (
    <>
      {error ? <Banner tone="bad">{error}</Banner> : null}
      {counts.all === 0 ? (
        <GigBoardEmpty myTrack={myTrack} trackLabels={trackLabels} />
      ) : (
        <GigBoardScreen
          myTrack={myTrack}
          trackLabels={trackLabels}
          home={home}
          tab={tab}
          filter={filter}
          tiles={tiles}
          total={total}
          sources={sources}
          updatedAt={updatedAt}
          counts={counts}
          loadingGigs={loadingGigs}
          gigsError={gigsError}
          onTab={setTab}
          onFilter={(patch) => {
            const next = { ...filter, ...patch, page: 1 };
            setFilter(next);
            if ("search" in patch) {
              seq.current++; // anything still loading is now stale
              loadSoon(next);
            } else void load(next, false);
          }}
          onShowMore={() => {
            const next = { ...filter, page: filter.page + 1 };
            setFilter(next);
            void load(next, true);
          }}
          onRetry={() => void load(filter, false)}
          onApplied={recordApply}
          onStatus={(application, status) =>
            void setApplicationStatus(application.id, status)
              .then((updated) => {
                replaceApplication(updated);
                if (status === "won") void navigate(`/gig/6/win?app=${updated.id}`);
              })
              .catch((e: unknown) => setError(messageOf(e)))
          }
        />
      )}
      {checkinOpen ? (
        <CheckinModal
          firstName={user.firstName}
          applications={home.checkinApplications}
          busy={checkinBusy}
          error={checkinError}
          onFinish={finishCheckin}
          onAddDetails={(application) => {
            setCheckinOpen(false);
            void navigate(`/gig/6/win?app=${application.id}`);
          }}
          onDone={() => {
            setCheckinOpen(false);
            // Friday's reminder links to ?checkin=1; drop it so a refresh doesn't look like a fresh tap.
            if (params.has("checkin")) void navigate("/gig/6", { replace: true });
          }}
        />
      ) : null}
    </>
  );
}

export function ReportWinRoute() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const appId = params.get("app");
  // Coming from an application ("Got it"): prefill from it, as v2's screenShowReportWin(appId) does.
  const loaded = useAsync(() => (appId ? fetchGigBoardHome() : Promise.resolve(null)), [appId]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<WinPaid | null>(null);

  if (saved) {
    return (
      <DoneScreen
        kicker="Win recorded"
        title="Congratulations on the work."
        sub={
          saved === "yes"
            ? "This counts toward your program results. Keep going: every win makes the next one easier."
            : "We'll check in again once you've been paid."
        }
        next={{ to: "/gig/6", label: "Back to the Gig Board" }}
      />
    );
  }

  const application = appId
    ? (loaded.data?.applications.find((a) => a.id === appId) ?? null)
    : null;

  return (
    <ScreenState loading={loaded.loading} error={loaded.error} onRetry={loaded.reload}>
      <ReportWinScreen
        key={application?.id ?? "new"}
        prefill={
          application
            ? { what: application.title, type: "Gig on a platform", via: "The Gig Board" }
            : undefined
        }
        busy={busy}
        error={error}
        onCancel={() => void navigate("/gig/6")}
        onSave={(draft: WinDraft, proof) =>
          void attempt(setBusy, setError, async () => {
            const win = await reportWin(toWinInput(draft, application?.id ?? null), proof);
            setSaved(win.paid);
          })
        }
      />
    </ScreenState>
  );
}
